import { Injectable } from '@angular/core';
import { IChatConversation, IChatConversationMessage, IConversationsUpdatedEvent } from '@fi-shared/fi-chat/fi-chat';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { PersonalNotificationsCategoryType } from '@fi-shared/types';
import { filter } from 'rxjs/operators';
import { fiTextToHtml, isDefined } from '@fi-shared/utils';
import { getTotalMilliseconds } from '@fi-shared/utils/get-total-milliseconds';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';

@Injectable()
export class FiChatSyncService {
    constructor(
        private userDataService: FiUserDataService,
        private appTextService: AppTextService,
    ) {}

    //region conversations
    private _conversations: IChatConversation[] = [];
    // don't allow to change conversations outside
    get conversations() {
        return this._conversations.slice();
    }
    get newConversations() {
        return this._conversations
            .slice()
            .filter(
                (conversation) =>
                    conversation.NotificationCategory === PersonalNotificationsCategoryType.New &&
                    !conversation.IsHidden,
            );
    }
    get conversationsCount() {
        return this._conversations.length;
    }
    findConversationById(conversationId: number) {
        return this._conversations.find((conversation) => conversation.Id === conversationId);
    }

    updateConversation(conversationId: number, callback: (conversation: IChatConversation) => void) {
        const currentConversation = this.findConversationById(conversationId);
        if (currentConversation) {
            callback(currentConversation);
            this.triggerConversationsUpdated({
                $event: {
                    changedConversationIds: [conversationId],
                },
            });
        }
    }

    triggerConversationsUpdated(args: {
        updatedConversations?: IChatConversation[];
        $event?: IConversationsUpdatedEvent;
    }) {
        if (!args) {
            args = {};
        }
        if (!args.$event) {
            args.$event = {};
        }
        if (args.updatedConversations) {
            const newConversationIds = args.updatedConversations.map((item) => item.Id);
            const removedConversationIds = this._conversations
                .filter((item) => newConversationIds.indexOf(item.Id) === -1)
                .map((item) => item.Id);
            removedConversationIds.forEach((removedConversationId) =>
                this._conversationRemoved$.next(removedConversationId),
            );

            this._conversations = args.updatedConversations;
        }

        this._conversationsUpdated$.next(args.$event);
    }

    private _conversationsUpdated$ = new ReplaySubject<IConversationsUpdatedEvent>(1);
    get conversationsUpdated$() {
        return this._conversationsUpdated$.asObservable();
    }
    conversationUpdated$(conversationId: number): Observable<IConversationsUpdatedEvent> {
        return this.conversationsUpdated$.pipe(
            filter(
                ($event) =>
                    $event.changedConversationIds && $event.changedConversationIds.indexOf(conversationId) !== -1,
            ),
        );
    }

    private _conversationRemoved$ = new Subject<number>();
    get conversationRemoved$() {
        return this._conversationRemoved$.asObservable();
    }
    //endregion

    //region conversation messages
    triggerConversationMessageUpdated(conversationMessage: IChatConversationMessage) {
        const currentConversation = this.findConversationById(conversationMessage.ConversationId);
        if (currentConversation) {
            // update LatestActivity
            if (
                !isDefined(currentConversation.LatestActivity) ||
                getTotalMilliseconds(conversationMessage.ChangedOn) >
                    getTotalMilliseconds(currentConversation.LatestActivity)
            ) {
                currentConversation.LatestActivityMessage = Object.assign({}, conversationMessage);

                currentConversation.LatestActivity = conversationMessage.ChangedOn;
                currentConversation.LatestActivityMessage.CreatedByUserName = conversationMessage.CreatedByUserName;
                currentConversation.LatestActivityMessage.UserProfile = conversationMessage.UserProfile;
                currentConversation.LatestActivityMessage.Type = conversationMessage.Type;
                currentConversation.LatestActivityMessage.Content =
                    this.getLatestConversationContent(conversationMessage);
            }
            if (conversationMessage.CreatedByUserId !== this.userDataService.getUserId())
                currentConversation.NotificationCategory = PersonalNotificationsCategoryType.New;

            this.triggerConversationsUpdated({
                $event: {
                    changedConversationIds: [currentConversation.Id],
                },
            });
        }
        this._conversationMessageUpdated$.next(conversationMessage);
    }

    getLatestConversationContent(conversationMessage: IChatConversationMessage): string {
        if (conversationMessage.Content) return conversationMessage.Content;

        if (conversationMessage.MediaBankFileReferences.length)
            return (
                conversationMessage.MediaBankFileReferences[0].Name +
                conversationMessage.MediaBankFileReferences[0].Extension
            );

        if (conversationMessage.Links.length)
            return conversationMessage.ChangedByUserId === this.userDataService.getUserId()
                ? this.appTextService.get('chat.conversation_message_latest_gif_self')
                : this.appTextService.get('chat.conversation_message_latest_gif', {
                      '{0}': fiTextToHtml(conversationMessage.UserFirstName),
                  });
    }

    private _conversationMessageUpdated$ = new Subject<IChatConversationMessage>();
    get conversationMessageUpdated$() {
        return this._conversationMessageUpdated$.asObservable();
    }
    //endregion
}
